import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { aws } from '../lib/awsdb';
import { contactDisplayName, contactSubtitle } from '../lib/contacts';
import ContactAutocomplete from '../lib/ContactAutocomplete';
import RelationshipDrawer from './RelationshipDrawer';

const SANS = 'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif';

function formatRelative(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  const m = Math.floor(diff / 60_000); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

export default function RelationshipsPanel({ session }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  // Mine vs Others toggle. "Mine" = relationships I own (owner_pin =
  // session.code). "Others" = everything else. RLS lets me read both
  // (cross-staff visibility per spec); only writes are owner-gated
  // (mig 126).
  const [scope, setScope] = useState('mine');
  // Per-row +Update button busy state. Maps relationship.id → bool.
  const [bumping, setBumping] = useState({});
  // Per-row inline compose state for +Update.
  //   composeOpenId: which relationship's compose box is open (null = none)
  //   composeText:   the note text being typed
  // Mo's complaint 2026-05-10: clicking +Update silently inserted body
  // "Update" with no chance to type a real note. The button now toggles
  // an inline composer; Save inserts with the typed body (falls back
  // to "Update" if the user submits empty), Cancel closes without
  // writing.
  const [composeOpenId, setComposeOpenId] = useState(null);
  const [composeText, setComposeText] = useState('');
  // Mig 139 (C1, 2026-05-10): staff_directory no longer exposes `code`
  // (it IS the JWT sub claim). The relationship_updates_view now returns
  // a `system`-aware author_name for every row, so the in-memory
  // staffByCode lookup map this component used to maintain is obsolete.

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Read from `relationships_view` — server-side projection that
      // hides raw owner_pin (PIN/auth credential) and exposes
      // owner_name + is_owner derived from JWT (mig 127). Writes still
      // go to the base table. RLS still applies (view is SECURITY
      // INVOKER).
      //
      // Sort priority: last_contact_at ASC NULLS FIRST — never-contacted
      // bubble to the top, then longest-ago first. This is Mo's
      // "who do I prioritise next?" sort. created_at is the secondary
      // tiebreak so newest relationships float above stale ones with
      // identical contact timestamps.
      const { data, error } = await aws.from('relationships_view')
        .select(`
          id, contact_id, archived, last_synced_at, last_contact_at, created_at,
          is_owner, owner_name,
          contact:contacts(id, contact_person, company_name, email, phone, category)
        `)
        .eq('archived', false)
        .order('last_contact_at', { ascending: true, nullsFirst: true })
        .order('created_at', { ascending: false });
      if (error) throw error;

      // Fan-out one fetch for the latest update per relationship. Could
      // become a view later; for now this is small (<100 rows expected).
      const ids = (data || []).map(r => r.id);
      let lastByRel = {};
      if (ids.length) {
        // Read from `relationship_updates_view` — hides author_pin,
        // exposes author_name + is_author (mig 127).
        const { data: ups } = await aws.from('relationship_updates_view')
          .select('id, relationship_id, body, source, author_name, is_author, created_at')
          .in('relationship_id', ids)
          .order('created_at', { ascending: false })
          .limit(ids.length * 3); // newest 3 per is plenty; we'll pick first
        for (const u of ups || []) {
          if (!lastByRel[u.relationship_id]) lastByRel[u.relationship_id] = u;
        }
      }

      const merged = (data || []).map(r => ({ ...r, last_update: lastByRel[r.id] || null }));
      setRows(merged);
    } catch (e) {
      console.warn('[RelationshipsPanel] load failed:', e?.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Apply the Mine/Others split + count both buckets so the toggle
  // shows accurate badges. Uses the view's `is_owner` boolean — the
  // raw owner_pin is no longer in the response (mig 127).
  const { mine, others, visibleRows } = useMemo(() => {
    const m = [];
    const o = [];
    for (const r of rows) {
      if (r.is_owner) m.push(r);
      else o.push(r);
    }
    return {
      mine: m,
      others: o,
      visibleRows: scope === 'mine' ? m : o,
    };
  }, [rows, scope]);

  const openRel = useMemo(() => rows.find(r => r.id === openId) || null, [rows, openId]);

  // Per-tile +Update: open an inline compose box on the tile. The
  // actual insert happens on Save (saveCompose). Click event bubbles
  // up to the tile's onClick — stopProp so the drawer doesn't open
  // behind the box. Owner-only on the server (RLS via author_pin =
  // jwt sub on insert), button hidden otherwise.
  const openCompose = useCallback((rel, e) => {
    e?.stopPropagation();
    e?.preventDefault();
    setComposeOpenId(rel.id);
    setComposeText('');
  }, []);

  const closeCompose = useCallback((e) => {
    e?.stopPropagation();
    e?.preventDefault();
    setComposeOpenId(null);
    setComposeText('');
  }, []);

  // Save the typed note. The trg_bump_last_contact_at trigger does the
  // timestamp bump for us. Empty submissions fall back to "Update" so
  // the legacy "just stamp it" workflow still works for users who hit
  // Save without typing.
  const saveCompose = useCallback(async (rel, e) => {
    e?.stopPropagation();
    e?.preventDefault();
    const trimmed = (composeText || '').trim();
    setBumping(prev => ({ ...prev, [rel.id]: true }));
    try {
      const { error } = await aws.from('relationship_updates').insert([{
        relationship_id: rel.id,
        author_pin: session.code,
        body: trimmed || 'Update',
        source: 'manual',
      }]);
      if (error) throw error;
      setComposeOpenId(null);
      setComposeText('');
      await load();
    } catch (err) {
      // Surface the failure so the click doesn't appear silent.
      console.warn('[RelationshipsPanel] +Update failed:', err?.message);
      alert(`Couldn't save update: ${err?.message || 'unknown error'}`);
    } finally {
      setBumping(prev => { const n = { ...prev }; delete n[rel.id]; return n; });
    }
  }, [load, session?.code, composeText]);

  if (loading) {
    return <div style={{ padding: 24, color: '#9A9B9E', fontSize: 13 }}>Loading relationships…</div>;
  }

  return (
    <>
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 14,
        flex: 1, minHeight: 0, overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingInline: 2, gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', background: '#EEECE5', border: '1px solid #E0DED6', borderRadius: 999, padding: 3, gap: 2 }}>
            {[
              { id: 'mine',   label: 'Mine',   count: mine.length },
              { id: 'others', label: 'Others', count: others.length },
            ].map(it => {
              const active = scope === it.id;
              return (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => setScope(it.id)}
                  style={{
                    border: 'none', background: active ? '#0F1419' : 'transparent',
                    color: active ? '#FAFAF8' : '#3E4148',
                    borderRadius: 999, padding: '5px 14px',
                    fontFamily: SANS, fontSize: 13, fontWeight: 400,
                    letterSpacing: '-0.005em', cursor: 'pointer',
                    transition: 'background 180ms, color 180ms',
                  }}
                >
                  {it.label}
                  <span style={{
                    display: 'inline-block', marginLeft: 8, minWidth: 16, padding: '0 5px',
                    fontFamily: 'JetBrains Mono, ui-monospace, monospace', fontSize: 9.5, letterSpacing: '0.06em',
                    borderRadius: 999, lineHeight: '15px', textAlign: 'center', verticalAlign: '1.5px',
                    background: active ? 'rgba(255,255,255,0.16)' : '#E0DED6',
                    color: active ? '#FAFAF8' : '#6E7278',
                  }}>{it.count}</span>
                </button>
              );
            })}
          </div>
          <div style={{ fontFamily: SANS, fontSize: 12, color: '#6E7278' }}>
            {visibleRows.length} {scope === 'mine' ? 'mine' : 'others'} · longest unspoken first
          </div>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>
            ＋ Add Relationship
          </button>
        </div>

        {visibleRows.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9A9B9E', fontFamily: SANS, fontSize: 13 }}>
            {scope === 'mine'
              ? <>No relationships you own yet. Click <b>Add Relationship</b> to start nurturing one.</>
              : <>No relationships from other staff to show.</>}
          </div>
        ) : (
          <ul style={{
            listStyle: 'none', margin: 0, padding: 0,
            display: 'grid', gap: 14,
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            alignContent: 'start', overflow: 'auto', paddingBottom: 12,
          }}>
            {visibleRows.map(r => {
              const c = r.contact || {};
              const last = r.last_update;
              const personLine = contactDisplayName(c, 'person') || '(unknown contact)';
              const subtitle = contactSubtitle(c, 'person');
              // +Update is an owner-only action. RLS would 403 otherwise
              // (mig 126); hiding the button gives clean UI feedback.
              // Uses view's is_owner boolean (mig 127) — no PIN compare.
              const isOwner = !!r.is_owner;
              const isAdmin = session?.role === 'admin';
              const canMarkSpoke = isOwner || isAdmin;
              const isBumping = !!bumping[r.id];
              const isComposing = composeOpenId === r.id;
              return (
                <motion.li
                  key={r.id}
                  layout
                  onClick={() => setOpenId(r.id)}
                  style={{
                    background: '#FFFFFF', border: '1px solid #E8E6E0', borderRadius: 10,
                    padding: '14px 16px 12px', display: 'flex', flexDirection: 'column',
                    gap: 8, position: 'relative', cursor: 'pointer', userSelect: 'none',
                    boxShadow: '0 1px 0 rgba(15,20,25,0.02)', minHeight: 170,
                    listStyle: 'none',
                  }}
                  whileHover={{ borderColor: '#D5D3CD', boxShadow: '0 2px 8px rgba(15,20,25,0.04)' }}
                  transition={{ duration: 0.14 }}
                >
                  {/* Top row: +Update (recency lives in the footer "auto · Xm ago") */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                    {canMarkSpoke && !isComposing && (
                      <button
                        type="button"
                        onClick={(e) => openCompose(r, e)}
                        disabled={isBumping}
                        title="Add a note about this relationship"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 9px', borderRadius: 999,
                          fontFamily: SANS, fontSize: 11, fontWeight: 500, letterSpacing: '-0.005em',
                          border: '1px solid #E0DED6', background: '#F4F3EF', color: '#3E4148',
                          cursor: isBumping ? 'wait' : 'pointer', transition: 'all 120ms ease',
                          opacity: isBumping ? 0.6 : 1,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(15,20,25,0.08)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                      >
                        +Update
                      </button>
                    )}
                  </div>

                  {/* Title */}
                  <h3 style={{
                    fontFamily: SANS, fontSize: 15, fontWeight: 500, letterSpacing: '-0.01em',
                    color: '#0F1419', margin: 0, lineHeight: 1.3, paddingRight: 8,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{personLine}</h3>

                  {subtitle ? (
                    <div style={{ fontFamily: SANS, fontSize: 12, color: '#9A9B9E', marginTop: -4 }}>
                      {subtitle}
                    </div>
                  ) : null}

                  {/* Inline compose box (replaces the body preview while
                      open). Stop click-prop on the wrapper so typing /
                      clicking inside doesn't bubble up to the tile's
                      onClick (which would open the drawer). */}
                  {isComposing ? (
                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 2 }}>
                      <textarea
                        autoFocus
                        value={composeText}
                        onChange={(e) => setComposeText(e.target.value)}
                        onKeyDown={(e) => {
                          // Cmd/Ctrl+Enter saves, Esc cancels.
                          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') saveCompose(r, e);
                          else if (e.key === 'Escape') closeCompose(e);
                        }}
                        placeholder="What happened? (Cmd+Enter to save, Esc to cancel)"
                        rows={3}
                        disabled={isBumping}
                        style={{
                          width: '100%', boxSizing: 'border-box', resize: 'vertical',
                          fontFamily: SANS, fontSize: 12.5, lineHeight: 1.45,
                          padding: '8px 10px', borderRadius: 8,
                          border: '1px solid #D5D3CD', background: '#FFFFFF', color: '#0F1419',
                        }}
                      />
                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        <button
                          type="button"
                          onClick={(e) => saveCompose(r, e)}
                          disabled={isBumping}
                          style={{
                            padding: '5px 12px', borderRadius: 999,
                            fontFamily: SANS, fontSize: 11, fontWeight: 500,
                            border: 'none', background: '#0F1419', color: '#FAFAF8',
                            cursor: isBumping ? 'wait' : 'pointer',
                            opacity: isBumping ? 0.6 : 1,
                          }}
                        >
                          {isBumping ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={closeCompose}
                          disabled={isBumping}
                          style={{
                            padding: '5px 12px', borderRadius: 999,
                            fontFamily: SANS, fontSize: 11, fontWeight: 500,
                            border: '1px solid #E0DED6', background: '#F4F3EF', color: '#6E7278',
                            cursor: isBumping ? 'wait' : 'pointer',
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : last ? (
                    <p style={{
                      fontFamily: SANS, fontSize: 12.5, lineHeight: 1.45, color: '#6E7278',
                      margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      overflow: 'hidden', textWrap: 'pretty',
                    }}>{last.body}</p>
                  ) : (
                    <p style={{
                      fontFamily: SANS, fontSize: 12.5, lineHeight: 1.45,
                      color: '#9A9B9E', fontStyle: 'italic', margin: 0,
                    }}>No updates yet. Open to add a note or run Harry.</p>
                  )}

                  {/* Footer */}
                  <div style={{
                    borderTop: '1px solid #ECEAE4', marginTop: 'auto', paddingTop: 10,
                    display: 'flex', alignItems: 'center', gap: 14, color: '#6E7278',
                    fontFamily: SANS, fontSize: 12,
                  }}>
                    {last ? (
                      <span>
                        {last.source === 'manual' ? `note by ${last.author_name || '—'}` : 'auto'} · {formatRelative(last.created_at)}
                      </span>
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                </motion.li>
              );
            })}
          </ul>
        )}
      </div>

      {openRel && (
        <RelationshipDrawer
          session={session}
          relationship={openRel}
          onClose={() => setOpenId(null)}
          onChanged={() => { load(); }}
        />
      )}

      {adding && (
        <AddRelationshipModal
          session={session}
          onClose={() => setAdding(false)}
          onCreated={() => { setAdding(false); load(); }}
        />
      )}
    </>
  );
}

// ── Add Relationship modal ──────────────────────────────────────────
//
// Single-step: pick a contact via the existing ContactAutocomplete, then
// insert a relationships row with owner_pin = session.code. RLS gates on
// owner_pin = jwt sub so the insert is automatically scoped to the
// caller. UNIQUE (contact_id, owner_pin) means a second add for the
// same contact returns a clear error.
function AddRelationshipModal({ session, onClose, onCreated }) {
  const [picked, setPicked] = useState(null);
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ContactAutocomplete sets `value` from typing OR from selecting a row.
  // It doesn't currently surface the selected row id, so we additionally
  // listen for an exact match against the contacts table on submit.
  async function resolveContact() {
    const q = (query || '').trim();
    if (!q) return null;
    // PostgREST .or() needs special-char escaping; do two simple eq
    // probes instead so commas/quotes/dots in names don't break.
    const { data: byPerson } = await aws.from('contacts')
      .select('id, contact_person, company_name, email')
      .eq('contact_person', q)
      .is('archived_at', null)
      .limit(1);
    if (byPerson && byPerson[0]) return byPerson[0];
    const { data: byCompany } = await aws.from('contacts')
      .select('id, contact_person, company_name, email')
      .eq('company_name', q)
      .is('archived_at', null)
      .limit(1);
    return byCompany && byCompany[0] ? byCompany[0] : null;
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      let contact = picked;
      if (!contact) contact = await resolveContact();
      if (!contact) {
        setError('Pick an existing contact from the list. (To add a new contact first, open Address Book.)');
        return;
      }
      const payload = {
        contact_id: contact.id,
        owner_pin: session.code,
      };
      const { error } = await aws.from('relationships').insert([payload]);
      if (error) {
        if (error.message && /duplicate key/i.test(error.message)) {
          setError('You already track this contact. Open it from the list.');
        } else {
          setError(error.message || 'Insert failed');
        }
        return;
      }
      onCreated();
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <aside className="drawer po-drawer" style={{ width: 420 }}>
        <div className="drawer-header">
          <div>
            <div className="drawer-title">Add Relationship</div>
            <div style={{ fontSize: 12, color: 'var(--text-subdued)', marginTop: 2 }}>
              Pick an existing contact. The relationship is owned by you.
            </div>
          </div>
          <button className="drawer-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="drawer-content">
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Contact</label>
          <ContactAutocomplete
            session={session}
            value={query}
            onChange={(v) => { setQuery(v); setPicked(null); }}
            placeholder="Type to search contacts…"
            autoFocus
          />

          {error ? (
            <div style={{ marginTop: 16, padding: '10px 12px', background: '#FBEAE5', color: '#A03422', borderRadius: 8, fontSize: 12 }}>
              {error}
            </div>
          ) : null}
        </div>
        <div className="drawer-footer">
          <button className="btn btn-secondary btn-full" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-full" disabled={saving} onClick={save}>
            {saving ? 'Saving…' : 'Add'}
          </button>
        </div>
      </aside>
    </>
  );
}
